import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Transforms } from 'slate';
import { ReactEditor, RenderElementProps, useSlateStatic } from 'slate-react';

const MIN_COLUMN_PX = 60; // minimum 60px per column

const ColumnsComponent = ({ attributes, children, element }: RenderElementProps) => {
  const editor = useSlateStatic();
  const containerRef = useRef<HTMLDivElement>(null);
  const colCount = element.columnCount || 2;

  // columnWidths stores px values for the first N-1 columns.
  // The last column always fills remaining space (1fr).
  const columnWidths: number[] = element.columnWidths || [];

  // On first render (no saved widths yet), measure container and set equal px widths
  const initialized = useRef(false);
  useEffect(() => {
    if (columnWidths.length > 0 || initialized.current) return;
    const container = containerRef.current;
    if (!container) return;
    initialized.current = true;
    const containerWidth = container.getBoundingClientRect().width;
    const equalWidth = Math.floor(containerWidth / colCount);
    const widths = Array.from({ length: colCount - 1 }, () => equalWidth);
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(editor, { columnWidths: widths } as any, { at: path });
  }, [columnWidths.length, colCount, editor, element]);

  const updateWidths = useCallback(
    (newWidths: number[]) => {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, { columnWidths: newWidths } as any, { at: path });
    },
    [editor, element],
  );

  // Build gridTemplateColumns: first N-1 columns as px, last as 1fr
  const gridCols =
    columnWidths.length > 0
      ? [...columnWidths.map((w) => `${w}px`), '1fr'].join(' ')
      : `repeat(${colCount}, 1fr)`;

  return (
    <div
      {...attributes}
      ref={containerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
      }}
      className="group/columns relative my-2 border border-dashed border-v2blueGray-300 rounded-lg"
    >
      {React.Children.map(children, (child, index) => (
        <React.Fragment key={index}>
          <div className="min-h-[40px] border border-dashed border-v2blueGray-200 rounded relative">
            {child}
          </div>
          {index < colCount - 1 && (
            <ResizeHandle
              containerRef={containerRef}
              index={index}
              columnWidths={columnWidths}
              onResize={updateWidths}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ResizeHandle = ({
  containerRef,
  index,
  columnWidths,
  onResize,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  index: number;
  columnWidths: number[];
  onResize: (widths: number[]) => void;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const container = containerRef.current;
      if (!container) return;

      setIsDragging(true);
      const startX = e.clientX;
      const startWidths = [...columnWidths];

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaPx = moveEvent.clientX - startX;
        const newWidth = Math.max(MIN_COLUMN_PX, startWidths[index] + deltaPx);

        const newWidths = [...startWidths];
        newWidths[index] = Math.round(newWidth);
        onResize(newWidths);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [containerRef, index, columnWidths, onResize],
  );

  // Position the handle: sum of all px widths up to and including this column
  // We need to account for padding/gaps, so use the actual DOM position
  const handleRef = useRef<HTMLDivElement>(null);
  const [handleLeft, setHandleLeft] = useState<string | undefined>(undefined);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || columnWidths.length === 0) return;
    // Sum of first (index+1) column widths
    const leftPx = columnWidths.slice(0, index + 1).reduce((a, b) => a + b, 0);
    setHandleLeft(`${leftPx}px`);
  }, [containerRef, columnWidths, index]);

  return (
    <div
      ref={handleRef}
      contentEditable={false}
      onMouseDown={handleMouseDown}
      style={{ left: handleLeft }}
      className={`absolute top-0 bottom-0 w-[9px] -translate-x-1/2 z-10 cursor-col-resize select-none ${
        isDragging ? 'opacity-100' : 'opacity-0 group-hover/columns:opacity-100 transition-opacity'
      }`}
    >
      <div
        className={`mx-auto w-[3px] h-full rounded-full ${
          isDragging ? 'bg-v2blue-500' : 'bg-v2blueGray-300 hover:bg-v2blue-500'
        } transition-colors`}
      />
    </div>
  );
};

export default ColumnsComponent;
